import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { X, Building2, MapPin, DollarSign, CheckCircle } from 'lucide-react';

export default function ClientForm({
  organizationId,
  onClientAdded,
  onClose,
  editingClient = null,
  useGlobalClients = false,
  currentUserId = null,
}) {
  const [formData, setFormData] = useState({
    companyName: editingClient?.companyName || '',
    contactPerson: editingClient?.contactPerson || '',
    email: editingClient?.email || '',
    phone: editingClient?.phone || '',
    website: editingClient?.website || '',

    address: editingClient?.address || '',
    city: editingClient?.city || '',
    state: editingClient?.state || '',
    country: editingClient?.country || '',
    postalCode: editingClient?.postalCode || '',

    industry: editingClient?.industry || '',
    businessType: editingClient?.businessType || '',
    foundedYear: editingClient?.foundedYear || '',
    employeeCount: editingClient?.employeeCount || '',
    annualRevenue: editingClient?.annualRevenue || '',

    taxId: editingClient?.taxId || '',
    registrationNumber: editingClient?.registrationNumber || '',
    kycStatus: editingClient?.kycStatus || 'pending',
    kycDocuments: editingClient?.kycDocuments || [],

    totalProjects: editingClient?.totalProjects || 0,
    totalValue: editingClient?.totalValue || 0,
    status: editingClient?.status || 'active',

    notes: editingClient?.notes || '',
    tags: editingClient?.tags || [],
  });

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Retail',
    'Manufacturing',
    'Real Estate',
    'Transportation',
    'Energy',
    'Media',
    'Consulting',
    'Other',
  ];

  const businessTypes = [
    'Sole Proprietorship',
    'Partnership',
    'Corporation',
    'LLC',
    'Non-Profit',
    'Government',
  ];

  const employeeRanges = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

  const revenueRanges = [
    'Under $100K',
    '$100K - $500K',
    '$500K - $1M',
    '$1M - $5M',
    '$5M - $10M',
    '$10M+',
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.contactPerson.trim()) newErrors.contactPerson = 'Contact person is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.industry) newErrors.industry = 'Industry is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (editingClient?.id) {
        const ref = useGlobalClients
          ? doc(firestore, 'companyClient', currentUserId, 'clients', editingClient.id)
          : doc(firestore, 'companyProfiles', organizationId, 'clients', editingClient.id);
        const updateData = { ...formData, updatedAt: new Date() };
        await updateDoc(ref, updateData);
        onClientAdded({ id: editingClient.id, ...editingClient, ...updateData });
        onClose();
        return;
      }

      const clientData = {
        ...formData,
        organizationId,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const docRef = await addDoc(
        useGlobalClients
          ? collection(firestore, 'companyClient', currentUserId, 'clients')
          : collection(firestore, 'companyProfiles', organizationId, 'clients'),
        clientData
      );

      const newClient = {
        id: docRef.id,
        ...clientData,
      };

      onClientAdded(newClient);
      onClose();
    } catch (error) {
      alert('Error adding client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const steps = [
    { id: 1, title: 'Basic Information', icon: Building2 },
    { id: 2, title: 'Address & Contact', icon: MapPin },
    { id: 3, title: 'Business Details', icon: DollarSign },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={e => handleInputChange('companyName', e.target.value)}
                className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                  errors.companyName ? 'border-red-300' : 'border-slate-300'
                }`}
                placeholder="Enter company name"
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Contact Person *
                </label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={e => handleInputChange('contactPerson', e.target.value)}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.contactPerson ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Full name"
                />
                {errors.contactPerson && (
                  <p className="mt-1 text-sm text-red-600">{errors.contactPerson}</p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Industry *</label>
                <select
                  value={formData.industry}
                  onChange={e => handleInputChange('industry', e.target.value)}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.industry ? 'border-red-300' : 'border-slate-300'
                  }`}
                >
                  <option value="">Select industry</option>
                  {industries.map(industry => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
                {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.email ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="contact@company.com"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => handleInputChange('phone', e.target.value)}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.phone ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={e => handleInputChange('website', e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                placeholder="https://www.company.com"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Street Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={e => handleInputChange('address', e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                placeholder="123 Business Street"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={e => handleInputChange('city', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="City"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  State/Province
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={e => handleInputChange('state', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="State"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={e => handleInputChange('country', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="Country"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Postal Code</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={e => handleInputChange('postalCode', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="12345"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Business Type
                </label>
                <select
                  value={formData.businessType}
                  onChange={e => handleInputChange('businessType', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                >
                  <option value="">Select business type</option>
                  {businessTypes.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Founded Year
                </label>
                <input
                  type="number"
                  value={formData.foundedYear}
                  onChange={e => handleInputChange('foundedYear', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="2020"
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Employee Count
                </label>
                <select
                  value={formData.employeeCount}
                  onChange={e => handleInputChange('employeeCount', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                >
                  <option value="">Select range</option>
                  {employeeRanges.map(range => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Annual Revenue
                </label>
                <select
                  value={formData.annualRevenue}
                  onChange={e => handleInputChange('annualRevenue', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                >
                  <option value="">Select range</option>
                  {revenueRanges.map(range => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Tax ID / EIN
                </label>
                <input
                  type="text"
                  value={formData.taxId}
                  onChange={e => handleInputChange('taxId', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="12-3456789"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Registration Number
                </label>
                <input
                  type="text"
                  value={formData.registrationNumber}
                  onChange={e => handleInputChange('registrationNumber', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="Business registration number"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Notes</label>
              <textarea
                value={formData.notes}
                onChange={e => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                placeholder="Additional notes about the client..."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-h-[90vh] w-[92%] max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl sm:w-full md:max-w-4xl"
      >
        <div className="sticky top-0 border-b border-slate-200 bg-white p-4 md:p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </h2>
            <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all ${
                      isActive
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : isCompleted
                          ? 'border-green-500 bg-green-500 text-white'
                          : 'border-slate-300 bg-white text-slate-400'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span
                    className={`ml-2 text-sm font-medium ${
                      isActive ? 'text-slate-900' : 'text-slate-500'
                    }`}
                  >
                    {step.title}
                  </span>
                  {index < steps.length - 1 && (
                    <div
                      className={`mx-4 h-px w-8 ${isCompleted ? 'bg-green-500' : 'bg-slate-300'}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-4 md:p-6">
          {renderStepContent()}

          <div className="mt-8 flex justify-between">
            <button
              onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              disabled={currentStep === 1}
              className="rounded-xl border border-slate-300 px-6 py-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>

            <div className="flex space-x-3">
              {currentStep < steps.length ? (
                <button
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  className="rounded-xl bg-slate-900 px-6 py-2 text-white hover:opacity-90"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center space-x-2 rounded-xl bg-slate-900 px-6 py-2 text-white hover:opacity-90 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Save Client</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
